import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux'
import { LinkedIn } from 'react-linkedin-login-oauth2';
import SignUpWithGoogle from './SignUpWithGoogle.js';
import ReCAPTCHA from 'react-google-recaptcha';
import validator from 'validator';
import config from '../../config/Constant';
import { postRequestWithoutAccessToken } from '../../helpers/apiHandler';
import { setLoginSuccess } from '../../store/actions/reduxAction';
import LeftSection from './LeftSection';
import CustomAlert from '../../components/CustomAlert';
import IconLinkedIn from '../../assets/linkedin-icon.png';
import IconEye from '../../assets/AlertIcons/icon-eye.svg';
import IconEyeClose from '../../assets/AlertIcons/icon-eye-close.svg';
import TermsOfServiceModel from '../StaticPages/TermsOfService/TermsOfServiceModel.js'
import { updateSignupForm } from '../../store/actions/reduxAction';

const baseUrl = process.env.PUBLIC_URL;
const white_label_perkeso = process.env.REACT_APP_WHITE_LABEL_PERKESO === 'true';

const Registration = () => {

  const captchaRef = useRef();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState({ error: false, message: '' });
  const [termCondition, setTermCondition] = useState(false);
  const [firstNameError, setFirstNameError] = useState({ error: false, message: '' });
  const [emailError, setEmailError] = useState({ error: false, message: '' });
  const [passwordError, setPasswordError] = useState({ error: false, message: '' });
  const [termConditionError, setTermConditionError] = useState({ error: false, message: '' });
  const [alertState, setAlertState] = useState({
    show: false,
    variant: 'danger',
    message: '',
  });

  const { email: signup_email, name: signup_name, password: signup_password, passwordVerify } = useSelector((state) => state.signup);

  const onHide = () => setShow(false);

  const handleSignUpSubmit = (evt) => {
    evt.preventDefault();
    const form = evt.currentTarget;

    if (validator.isEmpty(confirmPassword)) {
      setConfirmPasswordError({
        error: true,
        message: 'Please confirm your password',
      });
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError({
        error: true,
        message: 'Passwords do not match',
      });
      return;
    }
    if (form.checkValidity() === false) {
      evt.stopPropagation();
    }

    if (validator.isEmpty(firstName)) {
      setFirstNameError({
        error: true,
        message: 'Please enter your name',
      });
      return;
    }

    if (!validator.matches(firstName, /^[a-zA-Z ]+$/)) {
      setFirstNameError({
        error: true,
        message: 'Your name must not contain numbers.',
      });
      return;
    }

    if (validator.isEmpty(email)) {
      setEmailError({
        error: true,
        message: 'Please enter your email address',
      });
      return;
    }

    if (!validator.isEmail(email)) {
      setEmailError({
        error: true,
        message: 'Please enter valid email',
      });
      return;
    }

    if (validator.isEmpty(password)) {
      setPasswordError({
        error: true,
        message: 'Please enter your password',
      });
      return;
    }

    if (
      !validator.matches(
        password,
        /(?=[A-Za-z0-9@#$%^&+!=]+$)^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@#$%^&+!=]).*$/g
      )
    ) {
      setPasswordError({
        error: true,
        message:
          'Please choose a strong password that includes at least 1 lowercase and uppercase letter, a number, as well as a special character (!@#$%^&*)',
      });
      return;
    }

    if (!validator.isLength(password, { min: 8, max: undefined })) {
      setPasswordError({
        error: true,
        message: 'The password must be at least 8 characters long.',
      });
      return;
    }

    if (!termCondition) {
      setTermConditionError({
        error: true,
        message: '',
      });
      return;
    }

    // captcha config
    let have_recaptcha = false;
    const recaptchaValue = captchaRef.current.getValue();
    if (recaptchaValue.trim() === '') {
      // we dont have captcha
      have_recaptcha = false;
    } else {
      // we have
      have_recaptcha = true;
    }

    if (have_recaptcha) {
      const data = {
        name: firstName,
        email: email,
        password: password,
        'g-recaptcha-response': recaptchaValue,
      };

      setLoading(true);

      postRequestWithoutAccessToken('/auth/register', data, (res) => {
        if (res.success) {
          dispatch(setLoginSuccess(res));
          dispatch(updateSignupForm({ name: firstName, email: email, password: password, passwordVerify: password }));
          navigate('/email/verify/' + "check_email_for_token");
          captchaRef.current.reset();
        } else {
          setAlertState({
            show: true,
            message: res.message,
            variant: 'danger',
          });
          captchaRef.current.reset();
        }
        setLoading(false);
      });
    } else {
      setAlertState({
        show: true,
        variant: 'danger',
        message: 'Verify yourself via Captcha.',
      });
    }
  };

  const handleLinkedInAuth = (code) => {
    if (code) {
      setAlertState({
        show: true,
        variant: 'success',
        message: 'You have successfully logged in with your LinkedIn account!',
      });

      const token = code;

      const data = {
        social_platform: config.social_login_platforms.linkedin,
        token: token,
      };
      postRequestWithoutAccessToken('/auth/social-login', data, (res) => {
        if (res.success) {
          dispatch(setLoginSuccess(res));
          const { phone_active, active, access_token } = res.data;

          if (active && phone_active) return navigate('/dashboard');
          if (!active) return navigate(`/email/verify/${access_token?.token}`);
          if (!phone_active && active) return navigate(`/phone/verify/${access_token?.token}`);
        }
        if (res.error) {
          setAlertState({
            show: true,
            message: res.message,
            variant: 'danger',
          });
        }
      });
    }
  };

  const captchaOnExpired = () => {
    const frameElement = document.getElementsByTagName('iframe');
    if (frameElement && frameElement[0]) {
      setTimeout(() => {
        frameElement[0].height = 100;
      }, 500);
    }
  };

  useEffect(() => {
    if (white_label_perkeso) return navigate("/login")
    if (alertState.show) {
      setTimeout(() => {
        setAlertState({
          message: '',
          show: false,
          variant: '',
        });
      }, config.notify_time);
    }

    // Set state of signup form based on redux state
    if (signup_name && signup_name !== '') setFirstName(signup_name);
    if (signup_email && signup_email !== '') setEmail(signup_email);
    if (signup_password && signup_password !== '') setPassword(signup_password);
    if (passwordVerify && passwordVerify !== '') setConfirmPassword(passwordVerify);
  }, [alertState]);

  return (
    <div className="Authenticate Registration">
      <TermsOfServiceModel
        show={show}
        onHide={onHide}
      />
      <div className="container-auth-grid">
        <LeftSection />
        <div className="right-container">
          <div className="form-container-outter">
            <div className="form-container">
              <div className="form-top-section">
                <h3>Sign Up</h3>
                <div>
                  Already have an account? <Link to={`${baseUrl}/login`}>Sign In</Link>
                </div>
              </div>
              <Form noValidate onSubmit={handleSignUpSubmit}>
                <CustomAlert
                  message={alertState.message}
                  show={alertState.show}
                  variant={alertState.variant}
                  closeAlert={() => setAlertState({ show: false, variant: 'danger', message: '' })}
                />
                <Form.Group style={{ marginBottom: 20 }}>
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    placeholder="Your name"
                    name="firstName"
                    value={firstName}
                    onChange={(evt) => {
                      setFirstName(evt.target.value);
                      setFirstNameError({ error: false, message: '' });
                    }}
                    isInvalid={firstNameError.error}
                  />
                  <Form.Control.Feedback type="invalid">
                    {firstNameError.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group style={{ marginBottom: 20 }}>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    required
                    type="email"
                    placeholder="Your email"
                    name="email"
                    value={email}
                    onChange={(evt) => {
                      setEmail(evt.target.value);
                      setEmailError({ error: false, message: '' });
                    }}
                    isInvalid={emailError.error}
                  />
                  <Form.Control.Feedback type="invalid">{emailError.message}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group style={{ marginBottom: 30 }}>
                  <Form.Label>Password</Form.Label>
                  <InputGroup className="input-group-password" hasValidation>
                    <Form.Control
                      required
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Your Password"
                      name="password"
                      value={password}
                      onChange={(evt) => {
                        setPassword(evt.target.value);
                        setPasswordError({ error: false, message: '' });
                      }}
                      isInvalid={passwordError.error}
                    />
                    <InputGroup.Append>
                      <InputGroup.Text onClick={() => setShowPassword(!showPassword)}>
                        <img
                          src={showPassword ? IconEye : IconEyeClose}
                          width={24}
                          height={24}
                          alt="..."
                        />
                      </InputGroup.Text>
                    </InputGroup.Append>
                    <Form.Control.Feedback type="invalid">
                      {passwordError.message}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
                <Form.Group style={{ marginBottom: 30 }}>
                  <Form.Label>Confirm Password</Form.Label>
                  <InputGroup className="input-group-password" hasValidation>
                    <Form.Control
                      required
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm Your Password"
                      name="confirmPassword"
                      value={confirmPassword}
                      onChange={(evt) => {
                        setConfirmPassword(evt.target.value);
                        setConfirmPasswordError({ error: false, message: '' });
                      }}
                      isInvalid={confirmPasswordError.error}
                    />
                    <InputGroup.Append>
                      <InputGroup.Text onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                        <img
                          src={showConfirmPassword ? IconEye : IconEyeClose}
                          width={24}
                          height={24}
                          alt="..."
                        />
                      </InputGroup.Text>
                    </InputGroup.Append>
                    <Form.Control.Feedback type="invalid">
                      {confirmPasswordError.message}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
                <Form.Group className="captcha-wrapper" style={{ marginBottom: 30 }}>
                  <ReCAPTCHA
                    onExpired={captchaOnExpired}
                    ref={captchaRef}
                    sitekey={config.google_recaptcha_site_key}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Check
                    custom
                    required
                    id="termCondition"
                    type="checkbox"
                    label={
                      <>
                        I accept{' '}
                        <Button variant="link" onClick={() => setShow(true)}>
                          terms and conditions
                        </Button>
                      </>
                    }
                    checked={termCondition}
                    isInvalid={termConditionError.error}
                    onChange={(evt) => {
                      setTermCondition(evt.target.checked);
                      setTermConditionError({ error: false, message: '' });
                    }}
                  />
                </Form.Group>
                <Form.Group>
                  <Button
                    variant="primary"
                    type="submit"
                    className={termCondition ? '' : 'disabled'}
                    disabled={loading}
                    style={{ minWidth: '160px' }}
                  >
                    {loading && <Spinner animation="border" size="sm" className="mr-2" />}
                    Sign Up
                  </Button>
                </Form.Group>
              </Form>
              <div className="divider-with-text" style={{ margin: '40px 0 20px' }}>
                <span>Or Sign Up with</span>
              </div>
              <div className="form-other-login">
                <div className="social-login">
                  <SignUpWithGoogle />

                  <LinkedIn
                    clientId={config.social_login_appId.linkedin}
                    scope="openid,profile,email"
                    state=""
                    redirectUri={`${window.location.origin}/linkedin`}
                    onSuccess={handleLinkedInAuth}
                  >
                    {({ linkedInLogin }) => (
                      <button
                        className="btn btn-social linkedin-btn"
                        type="button"
                        onClick={linkedInLogin}
                      >
                        <img src={IconLinkedIn} alt="..." />
                        <span>Sign up with Linkedin</span>
                      </button>
                    )}
                  </LinkedIn>
                </div>
                <div className="copyright-section outline-text">
                  @{new Date().getFullYear()} All rights reserved by elfo
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


export default Registration
